import { useState } from 'react';
import { CreatePunchesResponse } from '../types';

/**
 * Shared submit lifecycle for the three "add punches" dialogs:
 * - disables Create and shows a spinner while the request is in flight;
 * - on a thrown error (e.g. a 400 for an inactive employee), stays open and surfaces the message;
 * - on a response with `failed.length > 0`, stays open showing the partial result (and every failed punch);
 * - on full success (`failed.length === 0`), calls `onClose`.
 */
export const useCreatePunches = <T>(onCreate: (arg: T) => Promise<CreatePunchesResponse>, onClose: () => void) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [partial, setPartial] = useState<CreatePunchesResponse | null>(null);

  const submit = async (arg: T) => {
    setSubmitting(true);
    setError(null);
    try {
      const response = await onCreate(arg);
      if (response.failed.length > 0) {
        setPartial(response);
      } else {
        onClose();
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create punches');
    } finally {
      setSubmitting(false);
    }
  };

  return { submitting, error, partial, submit };
};
